import { Radio, RadioProps, RadioGroupProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';

export interface RadioButtonProps extends RadioProps {
  /** Radio size (currently only M supported as per Figma) */
  size?: 'm';
}

export interface RadioGroupComponentProps extends Omit<
  RadioGroupProps,
  'size'
> {
  /** Radio size (currently only M supported as per Figma) */
  size?: 'm';
}

interface StyledRadioProps {
  $token: GlobalToken;
}

const StyledRadio = styled(Radio)<StyledRadioProps>`
  font-family: ${({ $token }) => $token.fontFamily};

  /* Radio circle */
  .ant-radio {
    .ant-radio-inner {
      width: 16px;
      height: 16px;
      border: ${({ $token }) => $token.lineWidth}px solid
        ${({ $token }) => $token.colorTextTertiary};
      background: transparent;
      transition: all ${({ $token }) => $token.motionDurationMid} ease;

      &::after {
        width: 8px;
        height: 8px;
        margin-block-start: -4px;
        margin-inline-start: -4px;
        background: ${({ $token }) => $token.colorPrimary};
      }
    }

    /* Hover state */
    &:hover .ant-radio-inner {
      border-color: ${({ $token }) => $token.colorPrimaryHover};
    }
  }

  /* Checked state */
  .ant-radio-checked {
    .ant-radio-inner {
      border-color: ${({ $token }) => $token.colorPrimary};
      background: transparent;

      &::after {
        transform: scale(1);
        opacity: 1;
      }
    }

    &:hover .ant-radio-inner {
      border-color: ${({ $token }) => $token.colorPrimaryHover};

      &::after {
        background: ${({ $token }) => $token.colorPrimaryHover};
      }
    }

    &::after {
      border: none;
    }
  }

  /* Disabled state */
  .ant-radio-disabled {
    .ant-radio-inner {
      background: ${({ $token }) => $token.colorBgLayout};
      border-color: ${({ $token }) => $token.colorBgContainer};
    }

    &.ant-radio-checked .ant-radio-inner {
      &::after {
        background: ${({ $token }) => $token.colorTextDisabled};
      }
    }
  }

  /* Focus */
  .ant-radio-input:focus + .ant-radio-inner {
    box-shadow: 0 0 0 2px ${({ $token }) => $token.colorPrimaryBg};
  }

  /* Label */
  & > span:last-child {
    padding-inline-start: ${({ $token }) => $token.paddingXS}px;
    color: ${({ $token }) => $token.colorText};
    font-size: ${({ $token }) => $token.fontSize}px;
  }

  &.ant-radio-wrapper-disabled > span:last-child {
    color: ${({ $token }) => $token.colorTextTertiary};
  }
`;

const StyledRadioGroup = styled(Radio.Group)<StyledRadioProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.paddingSM}px;
`;

/**
 * RadioButton component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { RadioButton, RadioGroup } from '@libertify/ui';
 *
 * // Single radio
 * <RadioButton>Option 1</RadioButton>
 *
 * // Radio group
 * <RadioGroup defaultValue="a">
 *   <RadioButton value="a">Option A</RadioButton>
 *   <RadioButton value="b">Option B</RadioButton>
 *   <RadioButton value="c" disabled>Option C</RadioButton>
 * </RadioGroup>
 * ```
 */
export const RadioButton = ({
  size: _size = 'm',
  children,
  ...props
}: RadioButtonProps) => {
  const { token } = theme.useToken();
  // Note: size prop reserved for future use
  return (
    <StyledRadio $token={token} {...props}>
      {children}
    </StyledRadio>
  );
};

/**
 * RadioGroup component for grouping RadioButtons
 *
 * Now uses Ant Design theme tokens for consistent styling.
 */
export const RadioGroup = ({
  size: _size = 'm',
  children,
  ...props
}: RadioGroupComponentProps) => {
  const { token } = theme.useToken();
  // Note: size prop reserved for future use
  return (
    <StyledRadioGroup $token={token} {...props}>
      {children}
    </StyledRadioGroup>
  );
};

export default RadioButton;
